import { useState, useEffect, useRef, useMemo, useCallback, forwardRef } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  useDroppable,
  useDraggable,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { AdminNavigation } from '@/components/AdminNavigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Printer, Search, Plus, X, Settings, Palette, LayoutGrid, Type, Save, FolderOpen, Trash2, Calendar, Tag, GripVertical, Download, FileImage, FileText, Loader2, ChevronUp, ChevronDown, Edit2, Layers, Zap, FileArchive, CheckSquare, Square, MousePointer2, Move, Undo2, Redo2, Star, DollarSign, AlertCircle, Clock, Upload, HardDrive, Eye, ZoomIn, ZoomOut, LayoutTemplate, Sparkles } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Grid, type CellComponentProps } from 'react-window';
import { useAuth } from '@/contexts/AuthContext';
import type { Product } from '@shared/schema';

// Import theme system
import {
  THEMES,
  DEFAULT_STORE_CONFIG,
  DEFAULT_HEADER_TITLE_CONFIG,
  SectionConfig,
  FlyerTheme,
  StoreConfig,
  HeaderTitleConfig,
  FlyerTemplate,
  DATE_PRESETS,
  CardSize,
  getCardSizeConfig,
  CardDesign,
  CARD_DESIGN_PRESETS,
  CardStyle,
  getCardStyleConfig,
  CARD_STYLE_PRESETS,
  PriceFormat,
  BgPattern,
  BG_PATTERN_PRESETS,
  DividerStyle,
  DIVIDER_STYLE_PRESETS,
  cloneSections,
  saveTemplate,
  getTemplates,
  deleteTemplate,
  generateTemplateId,
  PRODUCT_BADGES,
  BadgeType,
  getBadgeConfig,
  SavedFlyer,
  saveFlyer,
  getSavedFlyers,
  deleteSavedFlyer,
  generateFlyerId,
  getAppSettings,
  saveAppSettings,
  exportFlyerAsJSON,
  importFlyerFromJSON,
  backupAllData,
  restoreFromBackup,
  GALLERY_TEMPLATES,
  GalleryTemplate,
} from '@/components/flyer-themes';
import { SectionConfigurator } from '@/components/section-configurator';
import {
  StoreConfigDialog,
  HeaderTitleConfigDialog,
  renderHeader,
  renderFooter,
} from '@/components/flyer-header-footer';
import { BatchPriceDialog } from '@/components/batch-price-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface FlyerProduct extends Product {
  flyerPrice?: number;
  section: string;
  displayOrder: number;
  badge?: BadgeType;
}

const PRODUCT_GRID_COLUMNS = 4;

// Product card for the flyer
function FlyerProductCard({ product, onRemove, onUpdatePrice, onUpdateBadge, cardSize = 'normal', cardDesign = 'simple' }: {
  product: FlyerProduct;
  onRemove?: () => void;
  onUpdatePrice?: (price: number | undefined) => void;
  onUpdateBadge?: (badge: BadgeType | undefined) => void;
  cardSize?: CardSize;
  cardDesign?: CardDesign;
}) {
  const [imageError, setImageError] = useState(false);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editPrice, setEditPrice] = useState('');
  const price = product.flyerPrice ?? product.ourPrice;
  const config = getCardSizeConfig(cardSize);
  const hasCustomPrice = product.flyerPrice !== undefined && product.flyerPrice !== product.ourPrice;
  const badgeConfig = product.badge ? getBadgeConfig(product.badge) : null;

  const handlePriceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditPrice(price.toFixed(2));
    setIsEditingPrice(true);
  };

  const handlePriceSave = () => {
    const newPrice = parseFloat(editPrice);
    if (!isNaN(newPrice) && newPrice >= 0) {
      // If same as original, clear custom price
      if (Math.abs(newPrice - product.ourPrice) < 0.01) {
        onUpdatePrice?.(undefined);
      } else {
        onUpdatePrice?.(newPrice);
      }
    }
    setIsEditingPrice(false);
  };

  const handlePriceKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handlePriceSave();
    } else if (e.key === 'Escape') {
      setIsEditingPrice(false);
    }
  };

  // Render action buttons
  const renderActionButtons = () => {
    if (!onRemove && !onUpdateBadge) return null;
    return (
      <div className="absolute -top-1 -right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        {onUpdateBadge && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                <Tag className="w-2.5 h-2.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-32 p-1" align="end">
              <div className="space-y-0.5">
                {PRODUCT_BADGES.map((badge) => (
                  <button
                    key={badge.id}
                    onClick={() => onUpdateBadge(product.badge === badge.id ? undefined : badge.id)}
                    className={`w-full text-left text-xs px-2 py-1 rounded flex items-center gap-1 ${
                      product.badge === badge.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded ${badge.color}`} />
                    {badge.label}
                  </button>
                ))}
                {product.badge && (
                  <button
                    onClick={() => onUpdateBadge(undefined)}
                    className="w-full text-left text-xs px-2 py-1 rounded text-red-500 hover:bg-red-50"
                  >
                    Remove badge
                  </button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}
        {onRemove && (
          <button
            onClick={onRemove}
            className="bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  // Render price display
  const renderPriceDisplay = () => {
    if (isEditingPrice) {
      return (
        <div className="flex items-center gap-0.5">
          <span className={`${config.priceSize} text-red-600`}>$</span>
          <input
            type="number"
            step="0.01"
            value={editPrice}
            onChange={(e) => setEditPrice(e.target.value)}
            onBlur={handlePriceSave}
            onKeyDown={handlePriceKeyDown}
            autoFocus
            className={`w-12 ${config.priceSize} font-black text-red-600 border-b border-red-300 outline-none bg-transparent`}
          />
        </div>
      );
    }
    return (
      <button
        onClick={handlePriceClick}
        className={`font-black text-red-600 ${config.priceSize} hover:underline cursor-pointer text-left ${
          hasCustomPrice ? 'bg-yellow-100 px-0.5 rounded' : ''
        }`}
        title="Click to edit price"
      >
        ${price.toFixed(2)}
      </button>
    );
  };

  // ============ PHOTO-DOMINANT DESIGN ============
  if (cardDesign === 'photo') {
    // Larger image with overlay text at bottom
    const photoImageHeight = cardSize === 'compact' ? 'h-16' : cardSize === 'large' ? 'h-28' : 'h-20';
    return (
      <div className={`bg-white border border-gray-300 flex flex-col relative group ${config.padding} overflow-hidden`}>
        {renderActionButtons()}

        {/* Large image with overlay */}
        <div className={`relative ${photoImageHeight} flex-shrink-0`}>
          {product.imageUrl && !imageError ? (
            <img
              src={product.imageUrl}
              alt={product.description}
              onError={() => setImageError(true)}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <span className="text-2xl opacity-30">🛒</span>
            </div>
          )}

          {/* Badge on image */}
          {badgeConfig && (
            <div className={`absolute top-0 left-0 ${badgeConfig.color} text-white text-[7px] font-bold px-1.5 py-0.5`}>
              {badgeConfig.label}
            </div>
          )}

          {/* Price overlay on image */}
          <div className="absolute bottom-0 right-0 bg-red-600 text-white px-1 py-0.5">
            <span className={`font-black ${config.priceSize}`}>${price.toFixed(2)}</span>
          </div>
        </div>

        {/* Compact info below */}
        <div className="flex-1 min-h-0 pt-0.5">
          <p className={`font-bold text-gray-800 leading-tight line-clamp-1 ${config.titleSize}`}>
            {product.description}
          </p>
          {product.chineseName && (
            <p className={`text-gray-600 line-clamp-1 ${config.titleSize}`}>
              {product.chineseName}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ============ BADGE-FOCUSED DESIGN ============
  if (cardDesign === 'badge') {
    return (
      <div className={`bg-white border-2 ${badgeConfig ? 'border-red-400' : 'border-gray-300'} flex flex-col relative group ${config.padding}`}>
        {renderActionButtons()}

        {/* Large prominent badge */}
        {badgeConfig && (
          <div className={`${badgeConfig.color} text-white text-[8px] font-black px-2 py-1 text-center -mx-1 -mt-1 mb-1`}>
            ★ {badgeConfig.label} ★
          </div>
        )}

        {/* Image */}
        <div className={`relative bg-gray-100 flex-shrink-0 ${config.imageHeight}`}>
          {product.imageUrl && !imageError ? (
            <img
              src={product.imageUrl}
              alt={product.description}
              onError={() => setImageError(true)}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <span className="text-xl opacity-30">🛒</span>
            </div>
          )}
        </div>

        {/* Info with bold pricing */}
        <div className="flex-1 flex flex-col min-h-0">
          <p className={`font-bold text-gray-800 leading-tight line-clamp-2 ${config.titleSize}`}>
            {product.description}
          </p>
          <div className="mt-auto">
            {/* Large emphasized price */}
            <div className={`bg-red-600 text-white font-black text-center py-0.5 -mx-1 ${config.priceSize}`}>
              ${price.toFixed(2)}
            </div>
            {(hasCustomPrice || product.retailPrice > price) && (
              <div className={`text-center text-gray-400 line-through ${config.titleSize}`}>
                {hasCustomPrice ? `Was $${product.ourPrice.toFixed(2)}` : `Reg $${product.retailPrice.toFixed(2)}`}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============ SIMPLE DESIGN (default) ============
  return (
    <div className={`bg-white border border-gray-300 flex flex-col relative group ${config.padding}`}>
      {/* Badge */}
      {badgeConfig && (
        <div className={`absolute top-0 left-0 ${badgeConfig.color} text-white text-[6px] font-bold px-1 py-0.5 z-10`}>
          {badgeConfig.label}
        </div>
      )}

      {renderActionButtons()}

      {/* Image */}
      <div className={`relative bg-gray-100 flex-shrink-0 ${config.imageHeight}`}>
        {product.imageUrl && !imageError ? (
          <img
            src={product.imageUrl}
            alt={product.description}
            onError={() => setImageError(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200">
            <span className="text-xl opacity-30">🛒</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 flex flex-col min-h-0">
        <p className={`font-bold text-gray-800 leading-tight line-clamp-2 ${config.titleSize}`}>
          {product.description}
        </p>
        {product.chineseName && (
          <p className={`text-gray-600 ${config.titleSize}`}>
            {product.chineseName}
          </p>
        )}
        <div className="mt-auto">
          {renderPriceDisplay()}
          {(hasCustomPrice || product.retailPrice > price) && (
            <div className={`text-gray-400 line-through ${config.titleSize}`}>
              {hasCustomPrice ? `Was: $${product.ourPrice.toFixed(2)}` : `Reg: $${product.retailPrice.toFixed(2)}`}
            </div>
          )}
          <div className={`text-gray-500 ${config.titleSize}`}>
            {product.size}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ DRAGGABLE SIDEBAR THUMBNAIL (like in-store flyer) ============
function DraggableSidebarThumbnail({ product }: { product: Product }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar-${product.id}`,
    data: { type: 'sidebar', product },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      title={`${product.description}${product.chineseName ? ` (${product.chineseName})` : ''}`}
      className={`relative aspect-square cursor-grab active:cursor-grabbing rounded overflow-hidden border-2 hover:border-emerald-400 transition-colors ${
        isDragging ? 'opacity-50 border-emerald-500' : 'border-transparent'
      }`}
    >
      {product.imageUrl ? (
        <img
          src={product.imageUrl}
          alt={product.description}
          className="w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-2xl">
          🛒
        </div>
      )}
      {/* Price overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1 py-0.5">
        <span className="text-white text-xs font-bold">${product.ourPrice?.toFixed(2)}</span>
      </div>
    </div>
  );
}

type SidebarProductCellProps = {
  products: Product[];
};

function SidebarProductCell({
  ariaAttributes,
  columnIndex,
  rowIndex,
  style,
  products,
}: CellComponentProps<SidebarProductCellProps>) {
  const index = rowIndex * PRODUCT_GRID_COLUMNS + columnIndex;
  const product = products[index];
  if (!product) return null;

  return (
    <div style={style} className="p-0.5" {...ariaAttributes}>
      <DraggableSidebarThumbnail product={product} />
    </div>
  );
}

// ============ GRID CELL (Droppable + Draggable if has item) ============
function GridCell({
  sectionId,
  index,
  item,
  cardSize,
  cardDesign,
  cardStyle = 'flat',
  priceFormat,
  onRemove,
  onUpdatePrice,
  onUpdateBadge,
  selectionMode,
  isSelected,
  onToggleSelect,
}: {
  sectionId: string;
  index: number;
  item?: FlyerProduct;
  cardSize: CardSize;
  cardDesign: CardDesign;
  cardStyle?: CardStyle;
  priceFormat?: PriceFormat;
  onRemove: (productId: number) => void;
  onUpdatePrice: (productId: number, price: number | undefined) => void;
  onUpdateBadge: (productId: number, badge: BadgeType | undefined) => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}) {
  const cellId = `cell-${sectionId}-${index}`;
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: cellId,
    data: { type: 'cell', sectionId, index },
  });

  // Also make the item draggable if it exists
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: item ? `grid-${sectionId}-${item.id}` : `empty-${cellId}`,
    data: item ? { type: 'grid', item, sectionId, index } : undefined,
    disabled: !item,
  });

  const config = getCardSizeConfig(cardSize);
  const styleConfig = getCardStyleConfig(cardStyle);
  const badgeConfig = item?.badge ? getBadgeConfig(item.badge) : null;

  // Common cell wrapper styles
  const cellClassName = `relative transition-all duration-150 ${
    isOver
      ? 'border-4 border-emerald-500 bg-emerald-200 ring-4 ring-emerald-400/50 scale-105 shadow-lg'
      : item ? styleConfig.className : 'border-2 border-dashed border-gray-300'
  } ${item ? 'bg-white' : 'bg-gray-50'} ${
    isSelected ? 'ring-2 ring-blue-500' : ''
  }`;

  // Helper to render price with format options
  const renderPrice = () => {
    if (!item) return null;
    const currentPrice = item.flyerPrice ?? item.ourPrice;
    const originalPrice = item.ourPrice;
    const hasCustomPrice = item.flyerPrice !== undefined && item.flyerPrice !== null;
    const showOriginal = priceFormat?.showOriginalPrice && hasCustomPrice && originalPrice && originalPrice !== currentPrice;
    const savings = showOriginal ? originalPrice - (currentPrice || 0) : 0;

    return (
      <div className="flex flex-col items-end">
        {/* Original price strikethrough */}
        {showOriginal && (
          <span className="text-[8px] text-gray-500 line-through">
            ${originalPrice?.toFixed(2)}
          </span>
        )}
        {/* Current price */}
        <span className={`${config.priceSize} font-black text-red-600 ${hasCustomPrice ? 'bg-yellow-100 px-0.5 rounded' : ''}`}>
          ${currentPrice?.toFixed(2)}
        </span>
        {/* Savings */}
        {priceFormat?.showSavings && savings > 0 && (
          <span className="text-[7px] text-green-600 font-bold">
            SAVE ${savings.toFixed(2)}
          </span>
        )}
        {/* Unit price */}
        {priceFormat?.showUnitPrice && item.unit && (
          <span className="text-[7px] text-gray-500">
            {item.unit}
          </span>
        )}
      </div>
    );
  };

  // Common selection checkbox
  const renderSelectionCheckbox = () => selectionMode && item && (
    <button
      onClick={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
      className="absolute top-0 left-0 w-5 h-5 bg-blue-500 hover:bg-blue-600 rounded-br z-30 flex items-center justify-center"
    >
      {isSelected ? (
        <CheckSquare className="w-3 h-3 text-white" />
      ) : (
        <Square className="w-3 h-3 text-white" />
      )}
    </button>
  );

  // Common action buttons
  const renderActionButtons = () => item && (
    <div className="absolute top-0.5 right-0.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-20">
      <Popover>
        <PopoverTrigger asChild>
          <button className="bg-blue-500 text-white rounded p-0.5 shadow" onClick={(e) => e.stopPropagation()}>
            <Tag className="w-2.5 h-2.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-32 p-1" align="end">
          <div className="space-y-0.5">
            {PRODUCT_BADGES.map((badge) => (
              <button
                key={badge.id}
                onClick={() => onUpdateBadge(item.id, item.badge === badge.id ? undefined : badge.id)}
                className={`w-full text-left text-xs px-2 py-1 rounded flex items-center gap-1 ${
                  item.badge === badge.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                }`}
              >
                <div className={`w-2 h-2 rounded ${badge.color}`} />
                {badge.label}
              </button>
            ))}
            {item.badge && (
              <button
                onClick={() => onUpdateBadge(item.id, undefined)}
                className="w-full text-left text-xs px-2 py-1 rounded text-red-500 hover:bg-red-50"
              >
                Remove
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
        className="bg-red-500 hover:bg-red-600 text-white rounded p-0.5 shadow"
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </div>
  );

  // Empty cell content
  if (!item) {
    return (
      <div ref={setDropRef} className={cellClassName} style={{ aspectRatio: '4/5' }}>
        <div className="h-full flex items-center justify-center">
          {isOver ? (
            <div className="flex flex-col items-center text-emerald-700 animate-pulse">
              <Plus className="w-8 h-8" />
              <span className="text-xs font-bold">DROP HERE</span>
            </div>
          ) : (
            <Plus className="w-5 h-5 text-gray-300" />
          )}
        </div>
      </div>
    );
  }

  // ============ PHOTO-DOMINANT DESIGN ============
  if (cardDesign === 'photo') {
    return (
      <div ref={setDropRef} className={cellClassName} style={{ aspectRatio: '4/5' }}>
        <div
          ref={setDragRef}
          {...listeners}
          {...attributes}
          className={`h-full relative cursor-grab active:cursor-grabbing group ${isDragging ? 'opacity-50' : ''}`}
        >
          {renderSelectionCheckbox()}
          {renderActionButtons()}

          {/* Full-bleed image */}
          <div className="absolute inset-0 bg-gray-100 rounded overflow-hidden">
            {item.imageUrl ? (
              <img src={item.imageUrl} alt="" className="w-full h-full object-cover" draggable={false} loading="lazy" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-3xl bg-gray-200">🛒</div>
            )}
          </div>

          {/* Badge on image */}
          {badgeConfig && (
            <div className={`absolute top-0 left-0 ${badgeConfig.color} text-white text-[7px] font-bold px-1.5 py-0.5 z-10`}>
              {badgeConfig.label}
            </div>
          )}

          {/* Price overlay */}
          <div className="absolute bottom-0 right-0 bg-red-600 text-white px-1.5 py-0.5 rounded-tl z-10">
            <span className={`font-black ${config.priceSize}`}>${(item.flyerPrice ?? item.ourPrice)?.toFixed(2)}</span>
          </div>

          {/* Title overlay */}
          <div className="absolute bottom-0 left-0 right-12 bg-black/60 text-white px-1.5 py-1 z-10">
            <p className={`font-bold truncate ${config.titleSize}`}>{item.description}</p>
          </div>
        </div>
      </div>
    );
  }

  // ============ BADGE-FOCUSED DESIGN ============
  if (cardDesign === 'badge') {
    return (
      <div
        ref={setDropRef}
        className={`${cellClassName} ${badgeConfig ? 'border-red-400 border-2' : ''}`}
        style={{ aspectRatio: '4/5' }}
      >
        <div
          ref={setDragRef}
          {...listeners}
          {...attributes}
          className={`h-full flex flex-col cursor-grab active:cursor-grabbing group ${isDragging ? 'opacity-50' : ''}`}
        >
          {renderSelectionCheckbox()}
          {renderActionButtons()}

          {/* Large prominent badge header */}
          {badgeConfig && (
            <div className={`${badgeConfig.color} text-white text-[8px] font-black px-2 py-1 text-center`}>
              ★ {badgeConfig.label} ★
            </div>
          )}

          {/* Image */}
          <div className="flex-1 relative bg-gray-100 overflow-hidden min-h-0">
            {item.imageUrl ? (
              <img src={item.imageUrl} alt="" className="w-full h-full object-cover" draggable={false} loading="lazy" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xl bg-gray-200">🛒</div>
            )}
          </div>

          {/* Large centered price bar */}
          <div className={`bg-red-600 text-white font-black text-center py-1 ${config.priceSize}`}>
            ${(item.flyerPrice ?? item.ourPrice)?.toFixed(2)}
          </div>

          {/* Product info */}
          <div className="px-1 py-0.5 bg-white">
            <p className={`font-bold text-gray-800 truncate ${config.titleSize}`}>{item.description}</p>
            {item.flyerPrice && (
              <p className="text-gray-400 line-through text-[8px] text-center">
                Reg: ${item.ourPrice?.toFixed(2)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============ SIMPLE DESIGN (default) ============
  return (
    <div ref={setDropRef} className={cellClassName} style={{ aspectRatio: '4/5' }}>
      <div
        ref={setDragRef}
        {...listeners}
        {...attributes}
        className={`h-full flex flex-col cursor-grab active:cursor-grabbing group ${isDragging ? 'opacity-50' : ''}`}
      >
        {renderSelectionCheckbox()}
        {renderActionButtons()}

        {/* Badge corner */}
        {badgeConfig && (
          <div className={`absolute top-0 left-0 ${badgeConfig.color} text-white text-[6px] font-bold px-1 py-0.5 z-10`}>
            {badgeConfig.label}
          </div>
        )}

        {/* Image */}
        <div className={`flex-1 relative bg-gray-100 rounded-t overflow-hidden ${config.imageHeight}`}>
          {item.imageUrl ? (
            <img src={item.imageUrl} alt="" className="w-full h-full object-cover" draggable={false} loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl bg-gray-200">🛒</div>
          )}
          {/* Flyer price indicator */}
          {item.flyerPrice && (
            <div className="absolute bottom-0.5 left-0.5 bg-yellow-400 text-yellow-900 text-[6px] font-bold px-0.5 rounded">
              SALE
            </div>
          )}
        </div>

        {/* Info */}
        <div className={`${config.padding} bg-white rounded-b`}>
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <p className={`font-medium text-gray-800 truncate ${config.titleSize}`}>{item.description}</p>
              {item.chineseName && (
                <p className={`text-gray-500 truncate ${config.titleSize}`}>{item.chineseName}</p>
              )}
            </div>
            {renderPrice()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ DRAG OVERLAY (compact thumbnail) ============
function DragOverlayContent({ product }: { product: Product | FlyerProduct }) {
  return (
    <div className="w-16 h-16 bg-white border-2 border-emerald-500 rounded shadow-2xl rotate-6 overflow-hidden">
      {product.imageUrl ? (
        <img src={product.imageUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-xl">🛒</div>
      )}
    </div>
  );
}

// Dynamic Section component (no nested DndContext - uses GridCell)
function FlyerSection({
  section,
  products,
  onRemoveProduct,
  onUpdatePrice,
  onUpdateBadge,
  onEditSection,
  onClearSection,
  onAutoFill,
  globalCardDesign = 'simple',
  selectionMode = false,
  selectedProducts,
  onToggleSelect,
}: {
  section: SectionConfig;
  products: FlyerProduct[];
  onRemoveProduct: (productId: number) => void;
  onUpdatePrice: (productId: number, price: number | undefined) => void;
  onUpdateBadge: (productId: number, badge: BadgeType | undefined) => void;
  onEditSection?: () => void;
  onClearSection?: () => void;
  onAutoFill?: () => void;
  globalCardDesign?: CardDesign;
  selectionMode?: boolean;
  selectedProducts?: Set<string>;
  onToggleSelect?: (productId: number) => void;
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const totalSlots = section.cols * section.rows;
  const cardSize = section.cardSize || 'normal';
  const cardDesign = section.cardDesign || globalCardDesign;
  const cardStyle = section.cardStyle || 'flat';
  const priceFormat = section.priceFormat;

  // Create array of cells with items in correct positions (like in-store flyer)
  const cells = Array.from({ length: totalSlots }, (_, index) => {
    return products.find(item => item.displayOrder === index);
  });

  const filledCount = products.length;

  return (
    <div className="mb-2">
      {/* Section Header with Quick Actions */}
      <div className={`${section.headerColor} text-white px-2 py-0.5 flex items-center gap-2`}>
        <span className="font-black text-sm tracking-wide">{section.title}</span>
        <span className="text-xs opacity-80">{section.chineseTitle}</span>
        <span className="text-xs opacity-60 ml-1">({filledCount}/{totalSlots})</span>

        {/* Quick Actions */}
        <div className="ml-auto flex items-center gap-1 print:hidden">
          {/* Auto-fill button */}
          {onAutoFill && section.categoryFilter && filledCount < totalSlots && (
            <button
              onClick={onAutoFill}
              className="p-0.5 px-1 hover:bg-white/20 rounded text-white/80 hover:text-white flex items-center gap-0.5 text-[10px] font-medium"
              title={`Auto-fill with ${section.categoryFilter} products`}
            >
              <Zap className="w-3 h-3" />
              Fill
            </button>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-0.5 hover:bg-white/20 rounded text-white/80 hover:text-white"
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
          {onEditSection && (
            <button
              onClick={onEditSection}
              className="p-0.5 hover:bg-white/20 rounded text-white/80 hover:text-white"
              title="Edit section"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          )}
          {onClearSection && filledCount > 0 && (
            <button
              onClick={onClearSection}
              className="p-0.5 hover:bg-white/20 rounded text-white/80 hover:text-white"
              title="Clear products"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Products Grid - uses GridCell for each position */}
      {!isCollapsed && (
        <div
          className={`grid gap-1 p-1 ${
            section.bgPattern === 'gradient'
              ? `bg-gradient-to-br ${section.bgColor} to-white/50`
              : section.bgPattern === 'striped'
              ? `${section.bgColor} bg-[linear-gradient(45deg,rgba(255,255,255,0.1)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.1)_50%,rgba(255,255,255,0.1)_75%,transparent_75%)] bg-[length:8px_8px]`
              : section.bgPattern === 'dots'
              ? `${section.bgColor} bg-[radial-gradient(circle,rgba(255,255,255,0.15)_1px,transparent_1px)] bg-[length:6px_6px]`
              : section.bgColor
          }`}
          style={{
            gridTemplateColumns: `repeat(${section.cols}, minmax(0, 1fr))`,
          }}
        >
          {cells.map((item, index) => (
            <GridCell
              key={index}
              sectionId={section.id}
              index={index}
              item={item}
              cardSize={cardSize}
              cardDesign={cardDesign}
              cardStyle={cardStyle}
              priceFormat={priceFormat}
              onRemove={onRemoveProduct}
              onUpdatePrice={onUpdatePrice}
              onUpdateBadge={onUpdateBadge}
              selectionMode={selectionMode}
              isSelected={item ? selectedProducts?.has(`${section.id}-${item.id}`) : false}
              onToggleSelect={item && onToggleSelect ? () => onToggleSelect(item.id) : undefined}
            />
          ))}
        </div>
      )}

      {/* Section Divider */}
      {section.showDivider && (
        <div className={`mt-1 ${
          section.dividerStyle === 'dashed' ? 'border-b-2 border-dashed border-gray-300' :
          section.dividerStyle === 'dotted' ? 'border-b-2 border-dotted border-gray-400' :
          section.dividerStyle === 'wave' ? 'h-2 bg-[url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 20 4\'%3E%3Cpath d=\'M0 2 Q5 0 10 2 T20 2\' fill=\'none\' stroke=\'%23d1d5db\' stroke-width=\'1\'/%3E%3C/svg%3E")] bg-repeat-x' :
          'border-b-2 border-gray-300'
        }`} />
      )}
    </div>
  );
}

// Theme selector thumbnail
function ThemeThumbnail({ theme, isSelected, onClick }: {
  theme: FlyerTheme;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`p-2 rounded-lg border-2 transition-all ${
        isSelected
          ? 'border-emerald-500 bg-emerald-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="text-2xl mb-1">{theme.thumbnail}</div>
      <div className="text-xs font-medium">{theme.name}</div>
    </button>
  );
}

export default function AdminWeeklyFlyer() {
  const { user } = useAuth();
  const { toast } = useToast();
  const printRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Template state
  const [templates, setTemplates] = useState<FlyerTemplate[]>(() => getTemplates());
  const [templateName, setTemplateName] = useState('');

  // Saved flyers state (complete flyers with products)
  const [savedFlyers, setSavedFlyers] = useState<SavedFlyer[]>(() => getSavedFlyers());
  const [flyerName, setFlyerName] = useState('');

  // Bulk selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(() => new Set());
  // Key format: `${sectionId}-${productId}`

  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState<FlyerProduct[] | null>(null);

  // Global drag state (like in-store flyer)
  const [activeProduct, setActiveProduct] = useState<Product | FlyerProduct | null>(null);

  // Global drag sensors (like in-store flyer)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Theme state - MUST be declared before handlers that use recordSnapshot
  const [selectedTheme, setSelectedTheme] = useState<FlyerTheme>(THEMES[0]);
  const [sections, setSections] = useState<SectionConfig[]>(() =>
    cloneSections(THEMES[0].defaultSections)
  );
  const [storeConfig, setStoreConfig] = useState<StoreConfig>({ ...DEFAULT_STORE_CONFIG });

  // Dialog states
  const [showSectionConfig, setShowSectionConfig] = useState(false);
  const [showStoreConfig, setShowStoreConfig] = useState(false);
  const [showTitleConfig, setShowTitleConfig] = useState(false);
  const [showBatchPriceDialog, setShowBatchPriceDialog] = useState(false);
  const [batchPriceScope, setBatchPriceScope] = useState<'selected' | 'section' | 'all'>('selected');

  // Auto-save and draft recovery
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [showDraftRecoveryDialog, setShowDraftRecoveryDialog] = useState(false);
  const [draftData, setDraftData] = useState<any>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef(false);

  // Confirmation dialogs
  const [showLoadFlyerConfirm, setShowLoadFlyerConfirm] = useState(false);
  const [pendingLoadFlyer, setPendingLoadFlyer] = useState<SavedFlyer | null>(null);
  const [showClearSectionConfirm, setShowClearSectionConfirm] = useState(false);
  const [pendingClearSection, setPendingClearSection] = useState<string | null>(null);
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);

  // Validation state
  const [flyerNameError, setFlyerNameError] = useState(false);

  // Print preview mode
  const [printPreviewMode, setPrintPreviewMode] = useState(false);
  const [printScale, setPrintScale] = useState(100);

  // Template gallery
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [selectedGalleryCategory, setSelectedGalleryCategory] = useState<'all' | 'weekly' | 'holiday' | 'clearance' | 'special'>('all');

  // Selected section for adding products
  const [selectedSection, setSelectedSection] = useState<string>(() =>
    THEMES[0].defaultSections[0]?.id || ''
  );

  // Flyer state - products organized by section
  const [flyerProducts, setFlyerProducts] = useState<Record<string, FlyerProduct[]>>({});

  // Flyer metadata
  const [headerTitleConfig, setHeaderTitleConfig] = useState<HeaderTitleConfig>({ ...DEFAULT_HEADER_TITLE_CONFIG });
  const [flyerScale, setFlyerScale] = useState(1);
  const [globalCardDesign, setGlobalCardDesign] = useState<CardDesign>(() => getAppSettings().defaultCardDesign);
  const [validDates, setValidDates] = useState(() => {
    const now = new Date();
    const nextWeek = new Date(now);
    nextWeek.setDate(nextWeek.getDate() + 7);
    return {
      start: now.toISOString().split('T')[0],
      end: nextWeek.toISOString().split('T')[0],
    };
  });

  // Undo/Redo system
  interface FlyerSnapshot {
    flyerProducts: Record<string, FlyerProduct[]>;
    sections: SectionConfig[];
    headerTitleConfig: HeaderTitleConfig;
    storeConfig: StoreConfig;
    validDates: { start: string; end: string };
    globalCardDesign: CardDesign;
  }

  const MAX_HISTORY = 50;
  const historyRef = useRef<FlyerSnapshot[]>([]);
  const historyIndexRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const isRestoringRef = useRef(false);

  // Create a snapshot of current state
  const createSnapshot = useCallback((): FlyerSnapshot => ({
    flyerProducts: JSON.parse(JSON.stringify(flyerProducts)),
    sections: JSON.parse(JSON.stringify(sections)),
    headerTitleConfig: JSON.parse(JSON.stringify(headerTitleConfig)),
    storeConfig: JSON.parse(JSON.stringify(storeConfig)),
    validDates: { ...validDates },
    globalCardDesign,
  }), [flyerProducts, sections, headerTitleConfig, storeConfig, validDates, globalCardDesign]);

  // Record state change to history
  const recordSnapshot = useCallback(() => {
    if (isRestoringRef.current) return;

    const snapshot = createSnapshot();

    // Remove any redo states
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);

    // Add new snapshot
    historyRef.current.push(snapshot);

    // Limit history size
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift();
    } else {
      historyIndexRef.current++;
    }

    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(false);
  }, [createSnapshot]);

  // Undo action
  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;

    isRestoringRef.current = true;
    historyIndexRef.current--;
    const snapshot = historyRef.current[historyIndexRef.current];

    setFlyerProducts(snapshot.flyerProducts);
    setSections(snapshot.sections);
    setHeaderTitleConfig(snapshot.headerTitleConfig);
    setStoreConfig(snapshot.storeConfig);
    setValidDates(snapshot.validDates);
    setGlobalCardDesign(snapshot.globalCardDesign);

    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(true);

    // Allow next state changes to be recorded
    setTimeout(() => { isRestoringRef.current = false; }, 0);
  }, []);

  // Redo action
  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;

    isRestoringRef.current = true;
    historyIndexRef.current++;
    const snapshot = historyRef.current[historyIndexRef.current];

    setFlyerProducts(snapshot.flyerProducts);
    setSections(snapshot.sections);
    setHeaderTitleConfig(snapshot.headerTitleConfig);
    setStoreConfig(snapshot.storeConfig);
    setValidDates(snapshot.validDates);
    setGlobalCardDesign(snapshot.globalCardDesign);

    setCanUndo(true);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);

    // Allow next state changes to be recorded
    setTimeout(() => { isRestoringRef.current = false; }, 0);
  }, []);

  // Handle drag start (sidebar or grid item)
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const data = active.data.current;
    if (data?.type === 'sidebar') {
      setActiveProduct(data.product);
    } else if (data?.type === 'grid') {
      setActiveProduct(data.item);
    }
  }, []);

  // Handle drag end (like in-store flyer) - uses functional updates to avoid stale closures
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveProduct(null);
    const { active, over } = event;

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (!overData || overData.type !== 'cell') return;

    const targetSectionId = overData.sectionId as string;
    const targetIndex = overData.index as number;

    if (activeData?.type === 'sidebar') {
      // Adding new item from sidebar
      const product = activeData.product as Product;

      setFlyerProducts(prev => {
        // Check if cell is occupied
        const sectionProducts = prev[targetSectionId] || [];
        const existingItem = sectionProducts.find(i => i.displayOrder === targetIndex);

        if (existingItem) {
          // Cell is occupied, don't add
          return prev;
        }

        // Add product at specific position
        const flyerProduct: FlyerProduct = {
          ...product,
          section: targetSectionId,
          displayOrder: targetIndex,
        };

        return {
          ...prev,
          [targetSectionId]: [...sectionProducts, flyerProduct],
        };
      });

    } else if (activeData?.type === 'grid') {
      // Moving item within/between sections
      const movingItem = activeData.item as FlyerProduct;
      const sourceSectionId = activeData.sectionId as string;
      const sourceIndex = activeData.index as number;

      if (sourceSectionId === targetSectionId && sourceIndex === targetIndex) {
        return; // Same cell
      }

      setFlyerProducts(prev => {
        // Check if target is occupied
        const targetSectionProducts = prev[targetSectionId] || [];
        const targetItem = targetSectionProducts.find(i => i.displayOrder === targetIndex);

        const newProducts = { ...prev };

        // Handle source section
        const sourceProducts = [...(prev[sourceSectionId] || [])];
        const movingIdx = sourceProducts.findIndex(p => p.id === movingItem.id);

        if (movingIdx !== -1) {
          if (targetItem && sourceSectionId === targetSectionId) {
            // Swap within same section
            const targetIdx = sourceProducts.findIndex(p => p.id === targetItem.id);
            sourceProducts[movingIdx] = { ...sourceProducts[movingIdx], displayOrder: targetIndex };
            sourceProducts[targetIdx] = { ...sourceProducts[targetIdx], displayOrder: sourceIndex };
            newProducts[sourceSectionId] = sourceProducts;
          } else if (targetItem) {
            // Swap between sections
            newProducts[sourceSectionId] = sourceProducts.filter(p => p.id !== movingItem.id);
            newProducts[sourceSectionId].push({ ...targetItem, section: sourceSectionId, displayOrder: sourceIndex });

            const targetProducts = [...(prev[targetSectionId] || [])].filter(p => p.id !== targetItem.id);
            targetProducts.push({ ...movingItem, section: targetSectionId, displayOrder: targetIndex });
            newProducts[targetSectionId] = targetProducts;
          } else {
            // Move to empty cell
            sourceProducts.splice(movingIdx, 1);
            newProducts[sourceSectionId] = sourceProducts;

            const targetProducts = [...(prev[targetSectionId] || [])];
            targetProducts.push({ ...movingItem, section: targetSectionId, displayOrder: targetIndex });
            newProducts[targetSectionId] = targetProducts;
          }
        }

        return newProducts;
      });
    }

    // Record snapshot after drag operation
    setTimeout(recordSnapshot, 0);
  }, [recordSnapshot]);

  // Bulk action helpers
  const toggleProductSelection = (sectionId: string, productId: number) => {
    const key = `${sectionId}-${productId}`;
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedProducts(new Set());
    setSelectionMode(false);
  };

  const bulkDeleteSelected = () => {
    const count = selectedProducts.size;
    const newProducts = { ...flyerProducts };
    selectedProducts.forEach(key => {
      const [sectionId, productIdStr] = key.split('-');
      const productId = parseInt(productIdStr);
      if (newProducts[sectionId]) {
        newProducts[sectionId] = newProducts[sectionId].filter(p => p.id !== productId);
      }
    });
    setFlyerProducts(newProducts);
    toast({ title: `Deleted ${count} products`, description: 'Press Ctrl+Z to undo' });
    clearSelection();
    setTimeout(recordSnapshot, 0);
  };

  const bulkBadgeSelected = (badge: BadgeType | undefined) => {
    const newProducts = { ...flyerProducts };
    selectedProducts.forEach(key => {
      const [sectionId, productIdStr] = key.split('-');
      const productId = parseInt(productIdStr);
      if (newProducts[sectionId]) {
        newProducts[sectionId] = newProducts[sectionId].map(p =>
          p.id === productId ? { ...p, badge } : p
        );
      }
    });
    setFlyerProducts(newProducts);
    toast({ title: badge ? `Applied "${badge}" badge to ${selectedProducts.size} products` : `Removed badges from ${selectedProducts.size} products` });
    clearSelection();
    setTimeout(recordSnapshot, 0);
  };

  const bulkMoveSelected = (targetSectionId: string) => {
    // Collect products to move
    const productsToMove: FlyerProduct[] = [];
    selectedProducts.forEach(key => {
      const [sectionId, productIdStr] = key.split('-');
      const productId = parseInt(productIdStr);
      const product = flyerProducts[sectionId]?.find(p => p.id === productId);
      if (product && sectionId !== targetSectionId) {
        productsToMove.push({ ...product, section: targetSectionId });
      }
    });

    // Remove from original sections and add to target
    const newProducts = { ...flyerProducts };
    selectedProducts.forEach(key => {
      const [sectionId, productIdStr] = key.split('-');
      const productId = parseInt(productIdStr);
      if (sectionId !== targetSectionId && newProducts[sectionId]) {
        newProducts[sectionId] = newProducts[sectionId].filter(p => p.id !== productId);
      }
    });

    // Add to target section
    const existingInTarget = newProducts[targetSectionId] || [];
    newProducts[targetSectionId] = [
      ...existingInTarget,
      ...productsToMove.map((p, i) => ({ ...p, displayOrder: existingInTarget.length + i })),
    ];

    setFlyerProducts(newProducts);
    toast({ title: `Moved ${productsToMove.length} products` });
    clearSelection();
    setTimeout(recordSnapshot, 0);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape key - close print preview
      if (e.key === 'Escape' && printPreviewMode) {
        e.preventDefault();
        setPrintPreviewMode(false);
        return;
      }

      // Don't trigger shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      // Undo/Redo - Ctrl+Z / Ctrl+Shift+Z
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }

      // Save flyer - Ctrl+S
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && !e.shiftKey) {
        e.preventDefault();
        if (flyerName.trim()) {
          handleSaveFlyer();
        } else {
          toast({ title: 'Enter a flyer name first', variant: 'destructive' });
        }
        return;
      }

      // Save template - Ctrl+Shift+S
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && e.shiftKey) {
        e.preventDefault();
        if (templateName.trim()) {
          handleSaveTemplate();
        } else {
          toast({ title: 'Enter a template name first', variant: 'destructive' });
        }
        return;
      }

      // Delete selected - Delete or Backspace (only when not in input)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isInputField) {
        if (selectedProducts.size > 0) {
          e.preventDefault();
          bulkDeleteSelected();
        }
        return;
      }

      // Select all in section - Ctrl+A (only when not in input)
      if ((e.metaKey || e.ctrlKey) && e.key === 'a' && !isInputField) {
        e.preventDefault();
        if (selectedSection && flyerProducts[selectedSection]) {
          setSelectionMode(true);
          const allInSection = new Set(
            flyerProducts[selectedSection].map(p => `${selectedSection}-${p.id}`)
          );
          setSelectedProducts(allInSection);
          toast({ title: `Selected ${allInSection.size} products` });
        }
        return;
      }

      // Escape - Clear selection
      if (e.key === 'Escape') {
        if (selectedProducts.size > 0) {
          clearSelection();
        }
        return;
      }

      // Copy - Ctrl+C
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && !isInputField) {
        if (selectedProducts.size > 0) {
          e.preventDefault();
          const productsToCopy: FlyerProduct[] = [];
          selectedProducts.forEach(key => {
            const [sectionId, productIdStr] = key.split('-');
            const productId = parseInt(productIdStr);
            const product = flyerProducts[sectionId]?.find(p => p.id === productId);
            if (product) {
              productsToCopy.push({ ...product });
            }
          });
          setClipboard(productsToCopy);
          toast({ title: `Copied ${productsToCopy.length} products` });
        }
        return;
      }

      // Cut - Ctrl+X
      if ((e.metaKey || e.ctrlKey) && e.key === 'x' && !isInputField) {
        if (selectedProducts.size > 0) {
          e.preventDefault();
          const productsToCut: FlyerProduct[] = [];
          selectedProducts.forEach(key => {
            const [sectionId, productIdStr] = key.split('-');
            const productId = parseInt(productIdStr);
            const product = flyerProducts[sectionId]?.find(p => p.id === productId);
            if (product) {
              productsToCut.push({ ...product });
            }
          });
          setClipboard(productsToCut);
          bulkDeleteSelected();
          toast({ title: `Cut ${productsToCut.length} products` });
        }
        return;
      }

      // Paste - Ctrl+V
      if ((e.metaKey || e.ctrlKey) && e.key === 'v' && !isInputField) {
        if (clipboard && clipboard.length > 0 && selectedSection) {
          e.preventDefault();
          const existingProducts = flyerProducts[selectedSection] || [];
          const maxOrder = existingProducts.length > 0
            ? Math.max(...existingProducts.map(p => p.displayOrder)) + 1
            : 0;

          const pastedProducts = clipboard.map((p, i) => ({
            ...p,
            section: selectedSection,
            displayOrder: maxOrder + i,
          }));

          setFlyerProducts(prev => ({
            ...prev,
            [selectedSection]: [...(prev[selectedSection] || []), ...pastedProducts],
          }));
          toast({ title: `Pasted ${pastedProducts.length} products to ${sections.find(s => s.id === selectedSection)?.title || selectedSection}` });
          setTimeout(recordSnapshot, 0);
        }
        return;
      }

      // Duplicate - Ctrl+D
      if ((e.metaKey || e.ctrlKey) && e.key === 'd' && !isInputField) {
        if (selectedProducts.size > 0) {
          e.preventDefault();
          const newProducts = { ...flyerProducts };
          selectedProducts.forEach(key => {
            const [sectionId, productIdStr] = key.split('-');
            const productId = parseInt(productIdStr);
            const product = newProducts[sectionId]?.find(p => p.id === productId);
            if (product) {
              const maxOrder = Math.max(...(newProducts[sectionId]?.map(p => p.displayOrder) || [0])) + 1;
              newProducts[sectionId] = [
                ...newProducts[sectionId],
                { ...product, displayOrder: maxOrder },
              ];
            }
          });
          setFlyerProducts(newProducts);
          toast({ title: `Duplicated ${selectedProducts.size} products` });
          setTimeout(recordSnapshot, 0);
        }
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, flyerName, templateName, selectedProducts, selectedSection, flyerProducts, clipboard, sections, clearSelection, bulkDeleteSelected, recordSnapshot, printPreviewMode]);

  // Initialize history with first snapshot
  useEffect(() => {
    if (historyRef.current.length === 0) {
      const initialSnapshot = createSnapshot();
      historyRef.current.push(initialSnapshot);
      historyIndexRef.current = 0;
    }
  }, [createSnapshot]);

  // Set page title
  useEffect(() => {
    document.title = 'Weekly Flyer - Tops100';
  }, []);

  // Check for draft on mount (draft recovery)
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const savedDraft = localStorage.getItem('flyer-draft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        // Check if draft has meaningful content
        const hasFlyerProducts = draft.flyerProducts && Object.values(draft.flyerProducts).some((arr: any) => arr.length > 0);
        const hasSections = draft.sections && draft.sections.length > 0;
        if (hasFlyerProducts || hasSections) {
          setDraftData(draft);
          setShowDraftRecoveryDialog(true);
        }
      } catch (e) {
        console.error('Failed to parse draft:', e);
        localStorage.removeItem('flyer-draft');
      }
    }
  }, []);

  // Auto-save with debounce (2 seconds after last change)
  useEffect(() => {
    // Don't auto-save before initialization is complete
    if (!hasInitializedRef.current) return;

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save
    autoSaveTimeoutRef.current = setTimeout(() => {
      const draft = {
        flyerProducts,
        sections,
        headerTitleConfig,
        storeConfig,
        validDates,
        globalCardDesign,
        selectedTheme: selectedTheme.id,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem('flyer-draft', JSON.stringify(draft));
      setLastAutoSave(new Date());
    }, 2000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [flyerProducts, sections, headerTitleConfig, storeConfig, validDates, globalCardDesign, selectedTheme]);

  // Unsaved changes warning (beforeunload)
  useEffect(() => {
    const hasProducts = Object.values(flyerProducts).some(arr => arr.length > 0);

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasProducts) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [flyerProducts]);

  // Handle draft recovery
  const handleRestoreDraft = useCallback(() => {
    if (!draftData) return;

    // Restore theme
    const theme = THEMES.find(t => t.id === draftData.selectedTheme) || THEMES[0];
    setSelectedTheme(theme);

    // Restore state
    if (draftData.sections) setSections(draftData.sections);
    if (draftData.headerTitleConfig) setHeaderTitleConfig(draftData.headerTitleConfig);
    if (draftData.storeConfig) setStoreConfig(draftData.storeConfig);
    if (draftData.validDates) setValidDates(draftData.validDates);
    if (draftData.globalCardDesign) setGlobalCardDesign(draftData.globalCardDesign);
    if (draftData.flyerProducts) setFlyerProducts(draftData.flyerProducts);

    setShowDraftRecoveryDialog(false);
    setDraftData(null);
    toast({ title: 'Draft restored successfully' });
  }, [draftData, toast]);

  const handleDiscardDraft = useCallback(() => {
    localStorage.removeItem('flyer-draft');
    setShowDraftRecoveryDialog(false);
    setDraftData(null);
    toast({ title: 'Draft discarded' });
  }, [toast]);

  // Initialize flyerProducts when sections change
  useEffect(() => {
    setFlyerProducts(prev => {
      const newProducts: Record<string, FlyerProduct[]> = {};
      sections.forEach(section => {
        newProducts[section.id] = prev[section.id] || [];
      });
      return newProducts;
    });
  }, [sections]);

  // Update selected section when sections change
  useEffect(() => {
    if (sections.length > 0 && !sections.find(s => s.id === selectedSection)) {
      setSelectedSection(sections[0].id);
    }
  }, [sections, selectedSection]);

  // Get visible sections sorted by order
  const visibleSections = useMemo(() =>
    sections
      .filter(s => s.visible)
      .sort((a, b) => a.order - b.order),
    [sections]
  );

  // Group sections into pages based on pageBreakAfter
  const pages = useMemo(() => {
    const result: SectionConfig[][] = [];
    let currentPage: SectionConfig[] = [];

    visibleSections.forEach((section) => {
      currentPage.push(section);
      if (section.pageBreakAfter) {
        result.push(currentPage);
        currentPage = [];
      }
    });

    // Add remaining sections as last page
    if (currentPage.length > 0) {
      result.push(currentPage);
    }

    // If no pages, return at least one empty page
    return result.length > 0 ? result : [[]];
  }, [visibleSections]);

  // Check if we have multiple pages
  const isMultiPage = pages.length > 1;

  // Auto-scale calculation - ensures flyer fits on 8.5x11" page
  // For multi-page flyers, scale each page individually
  const calculateScale = useCallback(() => {
    if (!printRef.current) return;

    // For multi-page flyers, don't auto-scale (user controls content per page with page breaks)
    if (pages.length > 1) {
      setFlyerScale(1);
      return;
    }

    // Get the first (only) page element
    const pageEl = printRef.current.querySelector('.flyer-page') as HTMLElement;
    if (!pageEl) return;

    // Reset scale temporarily to measure true height
    const originalTransform = pageEl.style.transform;
    pageEl.style.transform = 'scale(1)';

    // 11 inches at 96 DPI = 1056px
    const targetHeight = 1056;
    const actualHeight = pageEl.scrollHeight;

    if (actualHeight > targetHeight) {
      const newScale = Math.max(0.5, targetHeight / actualHeight);
      setFlyerScale(newScale);
    } else {
      setFlyerScale(1);
    }

    // Restore transform (will be re-applied by React render)
    pageEl.style.transform = originalTransform;
  }, [pages.length]);

  // Recalculate scale when content changes
  useEffect(() => {
    // Small delay to allow DOM to update
    const timeout = setTimeout(calculateScale, 50);
    return () => clearTimeout(timeout);
  }, [flyerProducts, sections, headerTitleConfig, calculateScale]);

  // Also use ResizeObserver for dynamic updates
  useEffect(() => {
    if (!printRef.current) return;

    const observer = new ResizeObserver(() => {
      calculateScale();
    });

    observer.observe(printRef.current);
    return () => observer.disconnect();
  }, [calculateScale]);

  // Handle theme change
  const handleThemeChange = (theme: FlyerTheme) => {
    setSelectedTheme(theme);
    setSections(cloneSections(theme.defaultSections));
    // Preserve products in matching sections, clear others
    setFlyerProducts(prev => {
      const newProducts: Record<string, FlyerProduct[]> = {};
      theme.defaultSections.forEach(section => {
        newProducts[section.id] = prev[section.id] || [];
      });
      return newProducts;
    });
    setTimeout(recordSnapshot, 0);
  };

  // Fetch available products (with Weekly toggle enabled)
  const { data: availableProducts = [] } = useQuery({
    queryKey: ['weekly-flyer-products', searchQuery, categoryFilter],
    queryFn: async () => {
      const allProducts = await apiRequest<Product[]>('/api/products');
      return allProducts.filter(p => p.active && p.instoreFlyer);
    },
    staleTime: 30000,
  });

  // Filter products based on search, category, and section's category filter
  const filteredProducts = useMemo(() => {
    let filtered = availableProducts;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.description.toLowerCase().includes(query) ||
        p.chineseName?.toLowerCase().includes(query)
      );
    }

    // Apply global category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(p => p.category.toLowerCase() === categoryFilter.toLowerCase());
    }

    // Apply section's category filter if set
    const currentSection = sections.find(s => s.id === selectedSection);
    if (currentSection?.categoryFilter) {
      filtered = filtered.filter(p =>
        p.category.toLowerCase() === currentSection.categoryFilter!.toLowerCase()
      );
    }

    // Exclude products already in the flyer
    const usedIds = new Set(Object.values(flyerProducts).flat().map(p => p.id));
    filtered = filtered.filter(p => !usedIds.has(p.id));

    return filtered;
  }, [availableProducts, searchQuery, categoryFilter, flyerProducts, sections, selectedSection]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set(availableProducts.map(p => p.category));
    return Array.from(cats).sort();
  }, [availableProducts]);

  // Add product to section (optionally at a specific position)
  const addProductToSection = (product: Product, sectionId: string, position?: number) => {
    const currentProducts = flyerProducts[sectionId] || [];

    const flyerProduct: FlyerProduct = {
      ...product,
      section: sectionId,
      displayOrder: position ?? currentProducts.length,
    };

    // If position is specified, insert at that position
    if (position !== undefined && position < currentProducts.length) {
      const newProducts = [...currentProducts];
      newProducts.splice(position, 0, flyerProduct);
      // Update display orders
      newProducts.forEach((p, i) => p.displayOrder = i);
      setFlyerProducts(prev => ({
        ...prev,
        [sectionId]: newProducts,
      }));
    } else {
      // Add to end
      setFlyerProducts(prev => ({
        ...prev,
        [sectionId]: [...currentProducts, flyerProduct],
      }));
    }
    setTimeout(recordSnapshot, 0);
  };

  // Remove product from section
  const removeProductFromSection = (productId: number, sectionId: string) => {
    setFlyerProducts(prev => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).filter(p => p.id !== productId),
    }));
    setTimeout(recordSnapshot, 0);
  };

  // Update product price
  const updateProductPrice = (productId: number, sectionId: string, price: number | undefined) => {
    setFlyerProducts(prev => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).map(p =>
        p.id === productId ? { ...p, flyerPrice: price } : p
      ),
    }));
    setTimeout(recordSnapshot, 0);
  };

  // Update product badge
  const updateProductBadge = (productId: number, sectionId: string, badge: BadgeType | undefined) => {
    setFlyerProducts(prev => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).map(p =>
        p.id === productId ? { ...p, badge } : p
      ),
    }));
    setTimeout(recordSnapshot, 0);
  };

  // Reorder products in section
  const reorderProductsInSection = (sectionId: string, newProducts: FlyerProduct[]) => {
    setFlyerProducts(prev => ({
      ...prev,
      [sectionId]: newProducts,
    }));
    setTimeout(recordSnapshot, 0);
  };

  // Clear section products
  const clearSectionProducts = (sectionId: string) => {
    setFlyerProducts(prev => ({
      ...prev,
      [sectionId]: [],
    }));
    toast({ title: 'Section cleared' });
    setTimeout(recordSnapshot, 0);
  };

  // Auto-fill section with products from its category filter
  const autoFillSection = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section?.categoryFilter) return;

    const totalSlots = section.cols * section.rows;
    const currentProducts = flyerProducts[sectionId] || [];
    const emptySlots = totalSlots - currentProducts.length;
    if (emptySlots <= 0) return;

    // Get all product IDs already in the flyer
    const usedIds = new Set(Object.values(flyerProducts).flat().map(p => p.id));

    // Filter available products by section's category
    const matchingProducts = availableProducts.filter(p =>
      p.category.toLowerCase() === section.categoryFilter!.toLowerCase() &&
      !usedIds.has(p.id)
    );

    // Take only what we need to fill empty slots
    const productsToAdd = matchingProducts.slice(0, emptySlots);

    if (productsToAdd.length === 0) {
      toast({ title: 'No matching products available', variant: 'destructive' });
      return;
    }

    // Add products to section
    const newProducts: FlyerProduct[] = productsToAdd.map((p, i) => ({
      ...p,
      section: sectionId,
      displayOrder: currentProducts.length + i,
    }));

    setFlyerProducts(prev => ({
      ...prev,
      [sectionId]: [...(prev[sectionId] || []), ...newProducts],
    }));

    toast({ title: `Added ${productsToAdd.length} products to ${section.title}` });
    setTimeout(recordSnapshot, 0);
  };

  // Edit section - open config with specific section
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);

  // Template handlers
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast({ title: 'Please enter a template name', variant: 'destructive' });
      return;
    }

    // Capture thumbnail
    let thumbnail: string | undefined;
    if (printRef.current) {
      const firstPage = printRef.current.querySelector('.flyer-page') as HTMLElement;
      if (firstPage) {
        try {
          const canvas = await html2canvas(firstPage, {
            scale: 0.2, // Small thumbnail
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
          });
          thumbnail = canvas.toDataURL('image/jpeg', 0.6);
        } catch (e) {
          console.error('Failed to capture thumbnail:', e);
        }
      }
    }

    const template: FlyerTemplate = {
      id: generateTemplateId(),
      name: templateName.trim(),
      createdAt: new Date().toISOString(),
      themeId: selectedTheme.id,
      sections: cloneSections(sections),
      headerTitleConfig: { ...headerTitleConfig },
      storeConfig: { ...storeConfig },
      thumbnail,
    };
    saveTemplate(template);
    setTemplates(getTemplates());
    setTemplateName('');
    toast({ title: `Template "${template.name}" saved` });
  };

  const handleLoadTemplate = (template: FlyerTemplate) => {
    const theme = THEMES.find(t => t.id === template.themeId) || THEMES[0];
    setSelectedTheme(theme);
    setSections(cloneSections(template.sections));
    setHeaderTitleConfig({ ...template.headerTitleConfig });
    setStoreConfig({ ...template.storeConfig });
    setFlyerProducts({}); // Clear products when loading template
    toast({ title: `Template "${template.name}" loaded` });
  };

  const handleDeleteTemplate = (id: string) => {
    deleteTemplate(id);
    setTemplates(getTemplates());
    toast({ title: 'Template deleted' });
  };

  // Save complete flyer (with products)
  const handleSaveFlyer = () => {
    if (!flyerName.trim()) {
      setFlyerNameError(true);
      toast({ title: 'Please enter a flyer name', variant: 'destructive' });
      return;
    }
    setFlyerNameError(false);

    // Flatten all products into SavedFlyerProduct format
    const allProducts: SavedFlyer['products'] = [];
    Object.entries(flyerProducts).forEach(([sectionId, products]) => {
      products.forEach(p => {
        allProducts.push({
          productId: p.id,
          sectionId,
          displayOrder: p.displayOrder,
          flyerPrice: p.flyerPrice,
          badge: p.badge,
        });
      });
    });

    const flyer: SavedFlyer = {
      id: generateFlyerId(),
      name: flyerName.trim(),
      createdAt: new Date().toISOString(),
      themeId: selectedTheme.id,
      sections: cloneSections(sections),
      headerTitleConfig: { ...headerTitleConfig },
      storeConfig: { ...storeConfig },
      globalCardDesign,
      validDates: { ...validDates },
      products: allProducts,
    };

    saveFlyer(flyer);
    setSavedFlyers(getSavedFlyers());

    // Clear draft since we've saved
    localStorage.removeItem('flyer-draft');

    // Build validation warnings
    const warnings: string[] = [];
    if (allProducts.length === 0) warnings.push('No products in flyer');
    if (new Date(validDates.end) < new Date()) warnings.push('End date is in the past');

    if (warnings.length > 0) {
      toast({
        title: `Flyer "${flyer.name}" saved`,
        description: `${allProducts.length} products. Warnings: ${warnings.join(', ')}`
      });
    } else {
      toast({ title: `Flyer "${flyer.name}" saved with ${allProducts.length} products` });
    }
    setFlyerName('');
  };

  // Load saved flyer (with products) - with confirmation
  const requestLoadFlyer = (flyer: SavedFlyer) => {
    const hasProducts = Object.values(flyerProducts).some(arr => arr.length > 0);
    if (hasProducts) {
      setPendingLoadFlyer(flyer);
      setShowLoadFlyerConfirm(true);
    } else {
      doLoadFlyer(flyer);
    }
  };

  const doLoadFlyer = (flyer: SavedFlyer) => {
    // Restore theme and settings
    const theme = THEMES.find(t => t.id === flyer.themeId) || THEMES[0];
    setSelectedTheme(theme);
    setSections(cloneSections(flyer.sections));
    setHeaderTitleConfig({ ...flyer.headerTitleConfig });
    setStoreConfig({ ...flyer.storeConfig });
    setGlobalCardDesign(flyer.globalCardDesign || 'simple');
    setValidDates({ ...flyer.validDates });

    // Restore products
    const restoredProducts: Record<string, FlyerProduct[]> = {};
    flyer.products.forEach(savedProduct => {
      // Find the full product data
      const product = availableProducts.find(p => p.id === savedProduct.productId);
      if (product) {
        if (!restoredProducts[savedProduct.sectionId]) {
          restoredProducts[savedProduct.sectionId] = [];
        }
        restoredProducts[savedProduct.sectionId].push({
          ...product,
          section: savedProduct.sectionId,
          displayOrder: savedProduct.displayOrder,
          flyerPrice: savedProduct.flyerPrice,
          badge: savedProduct.badge,
        });
      }
    });

    // Sort by displayOrder within each section
    Object.values(restoredProducts).forEach(products => {
      products.sort((a, b) => a.displayOrder - b.displayOrder);
    });

    setFlyerProducts(restoredProducts);
    toast({ title: `Flyer "${flyer.name}" loaded with ${flyer.products.length} products` });
  };

  const confirmLoadFlyer = () => {
    if (pendingLoadFlyer) {
      doLoadFlyer(pendingLoadFlyer);
      setPendingLoadFlyer(null);
    }
    setShowLoadFlyerConfirm(false);
  };

  const handleDeleteFlyer = (id: string) => {
    deleteSavedFlyer(id);
    setSavedFlyers(getSavedFlyers());
    toast({ title: 'Flyer deleted' });
  };

  // Export current flyer as JSON
  const handleExportCurrentFlyer = () => {
    // Build the flyer object from current state
    const allProducts: SavedFlyer['products'] = [];
    Object.entries(flyerProducts).forEach(([sectionId, products]) => {
      products.forEach(p => {
        allProducts.push({
          productId: p.id,
          sectionId,
          displayOrder: p.displayOrder,
          flyerPrice: p.flyerPrice,
          badge: p.badge,
        });
      });
    });

    const flyer: SavedFlyer = {
      id: generateFlyerId(),
      name: flyerName.trim() || `Exported-${new Date().toISOString().split('T')[0]}`,
      createdAt: new Date().toISOString(),
      themeId: selectedTheme.id,
      sections: cloneSections(sections),
      headerTitleConfig: { ...headerTitleConfig },
      storeConfig: { ...storeConfig },
      globalCardDesign,
      validDates: { ...validDates },
      products: allProducts,
    };

    exportFlyerAsJSON(flyer);
    toast({ title: 'Flyer exported as JSON' });
  };

  // Import flyer from JSON file
  const handleImportFlyer = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const importedFlyer = await importFlyerFromJSON(file);
      saveFlyer(importedFlyer);
      setSavedFlyers(getSavedFlyers());
      toast({ title: `Imported "${importedFlyer.name}" - you can now load it from saved flyers` });
    } catch (err) {
      toast({ title: 'Import failed', description: (err as Error).message, variant: 'destructive' });
    }

    // Reset file input
    e.target.value = '';
  };

  // Backup all data
  const handleBackup = () => {
    backupAllData();
    toast({ title: 'Backup downloaded successfully' });
  };

  // Restore from backup
  const handleRestore = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await restoreFromBackup(file);
      setSavedFlyers(getSavedFlyers());
      setTemplates(getTemplates());
      toast({
        title: 'Restore complete',
        description: `Added ${result.flyers} flyers and ${result.templates} templates`
      });
    } catch (err) {
      toast({ title: 'Restore failed', description: (err as Error).message, variant: 'destructive' });
    }

    // Reset file input
    e.target.value = '';
  };

  // Batch price adjustment
  const handleBatchPriceApply = (mode: 'fixed' | 'percent' | 'amount', value: number) => {
    const newProducts = { ...flyerProducts };
    let count = 0;

    const adjustPrice = (currentPrice: number | undefined | null) => {
      const basePrice = currentPrice ?? 0;
      switch (mode) {
        case 'fixed':
          return Math.max(0, value);
        case 'percent':
          return Math.max(0, basePrice * (1 + value / 100));
        case 'amount':
          return Math.max(0, basePrice + value);
      }
    };

    if (batchPriceScope === 'selected') {
      // Apply to selected products only
      selectedProducts.forEach(key => {
        const [sectionId, productIdStr] = key.split('-');
        const productId = parseInt(productIdStr);
        if (newProducts[sectionId]) {
          newProducts[sectionId] = newProducts[sectionId].map(p => {
            if (p.id === productId) {
              count++;
              return { ...p, flyerPrice: adjustPrice(p.flyerPrice ?? p.ourPrice) };
            }
            return p;
          });
        }
      });
    } else if (batchPriceScope === 'section') {
      // Apply to all products in selected section
      if (selectedSection && newProducts[selectedSection]) {
        newProducts[selectedSection] = newProducts[selectedSection].map(p => {
          count++;
          return { ...p, flyerPrice: adjustPrice(p.flyerPrice ?? p.ourPrice) };
        });
      }
    } else {
      // Apply to all products in flyer
      Object.keys(newProducts).forEach(sectionId => {
        newProducts[sectionId] = newProducts[sectionId].map(p => {
          count++;
          return { ...p, flyerPrice: adjustPrice(p.flyerPrice ?? p.ourPrice) };
        });
      });
    }

    setFlyerProducts(newProducts);
    const modeLabel = mode === 'fixed' ? `$${value.toFixed(2)}` :
                     mode === 'percent' ? `${value > 0 ? '+' : ''}${value}%` :
                     `${value > 0 ? '+' : ''}$${value.toFixed(2)}`;
    toast({ title: `Updated prices for ${count} products (${modeLabel})` });
    clearSelection();
    setTimeout(recordSnapshot, 0);
  };

  // Get product count for batch price dialog
  const getBatchPriceProductCount = () => {
    if (batchPriceScope === 'selected') {
      return selectedProducts.size;
    } else if (batchPriceScope === 'section') {
      return flyerProducts[selectedSection]?.length || 0;
    } else {
      return Object.values(flyerProducts).reduce((sum, products) => sum + products.length, 0);
    }
  };

  // Clear all products
  const handleClearAllProducts = () => {
    setFlyerProducts({});
    toast({ title: 'All products cleared' });
    setTimeout(recordSnapshot, 0);
  };

  // Apply gallery template
  const handleApplyGalleryTemplate = (template: GalleryTemplate) => {
    // Find the theme
    const theme = THEMES.find(t => t.id === template.themeId) || THEMES[0];

    // Apply theme
    setSelectedTheme(theme);

    // Apply sections (clone to avoid mutations)
    setSections(cloneSections(template.sections));

    // Apply header title config
    setHeaderTitleConfig(prev => ({
      ...prev,
      ...template.headerTitleConfig,
    }));

    // Clear existing products
    setFlyerProducts({});

    // Close gallery
    setShowTemplateGallery(false);

    toast({ title: `Applied "${template.name}" template` });
    setTimeout(recordSnapshot, 0);
  };

  // Apply date preset
  const handleDatePreset = (presetId: string) => {
    const preset = DATE_PRESETS.find(p => p.id === presetId);
    if (preset) {
      setValidDates(preset.getRange());
      setTimeout(recordSnapshot, 0);
    }
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Export to PDF
  const handleExportPDF = async () => {
    if (!printRef.current || isExporting) return;
    setIsExporting(true);

    try {
      // Get all page elements
      const pageElements = printRef.current.querySelectorAll('.flyer-page');

      if (pageElements.length === 0) {
        toast({ title: 'No pages to export', variant: 'destructive' });
        return;
      }

      // Letter size dimensions in inches
      const PAGE_WIDTH = 8.5;
      const PAGE_HEIGHT = 11;

      // Create PDF (letter size: 8.5 x 11 inches)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'letter',
      });

      // Process each page
      for (let i = 0; i < pageElements.length; i++) {
        const pageEl = pageElements[i] as HTMLElement;

        // Store original styles and set fixed dimensions for export
        const originalTransform = pageEl.style.transform;
        const originalWidth = pageEl.style.width;
        const originalMinHeight = pageEl.style.minHeight;
        const originalHeight = pageEl.style.height;
        const originalOverflow = pageEl.style.overflow;

        // Set exact dimensions for export - force letter size
        pageEl.style.transform = 'none';
        pageEl.style.width = `${PAGE_WIDTH}in`;
        pageEl.style.height = `${PAGE_HEIGHT}in`;
        pageEl.style.minHeight = `${PAGE_HEIGHT}in`;
        pageEl.style.overflow = 'hidden';

        const canvas = await html2canvas(pageEl, {
          scale: 2, // High quality
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: pageEl.offsetWidth,
          height: pageEl.offsetHeight,
          windowWidth: pageEl.offsetWidth,
          windowHeight: pageEl.offsetHeight,
        });

        // Restore original styles
        pageEl.style.transform = originalTransform;
        pageEl.style.width = originalWidth;
        pageEl.style.minHeight = originalMinHeight;
        pageEl.style.height = originalHeight;
        pageEl.style.overflow = originalOverflow;

        const imgData = canvas.toDataURL('image/png');

        // Add new page for all pages after the first
        if (i > 0) {
          pdf.addPage();
        }

        // Calculate proper scaling to fit letter size while maintaining aspect ratio
        const canvasAspect = canvas.width / canvas.height;
        const pageAspect = PAGE_WIDTH / PAGE_HEIGHT;

        let imgWidth = PAGE_WIDTH;
        let imgHeight = PAGE_HEIGHT;
        let offsetX = 0;
        let offsetY = 0;

        if (canvasAspect > pageAspect) {
          // Canvas is wider - fit to width
          imgWidth = PAGE_WIDTH;
          imgHeight = PAGE_WIDTH / canvasAspect;
          offsetY = (PAGE_HEIGHT - imgHeight) / 2;
        } else {
          // Canvas is taller - fit to height
          imgHeight = PAGE_HEIGHT;
          imgWidth = PAGE_HEIGHT * canvasAspect;
          offsetX = (PAGE_WIDTH - imgWidth) / 2;
        }

        pdf.addImage(imgData, 'PNG', offsetX, offsetY, imgWidth, imgHeight);
      }

      pdf.save(`flyer-${validDates.start}-${validDates.end}.pdf`);
      toast({ title: `PDF exported successfully (${pageElements.length} page${pageElements.length > 1 ? 's' : ''})` });
    } catch (error) {
      console.error('Export failed:', error);
      toast({ title: 'Export failed', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  // Export to PNG
  const handleExportPNG = async () => {
    if (!printRef.current || isExporting) return;
    setIsExporting(true);

    try {
      // Get all page elements
      const pageElements = printRef.current.querySelectorAll('.flyer-page');

      if (pageElements.length === 0) {
        toast({ title: 'No pages to export', variant: 'destructive' });
        return;
      }

      // Letter size dimensions in inches
      const PAGE_WIDTH = 8.5;
      const PAGE_HEIGHT = 11;

      // Process each page
      for (let i = 0; i < pageElements.length; i++) {
        const pageEl = pageElements[i] as HTMLElement;

        // Store original styles and set fixed dimensions for export
        const originalTransform = pageEl.style.transform;
        const originalWidth = pageEl.style.width;
        const originalMinHeight = pageEl.style.minHeight;
        const originalHeight = pageEl.style.height;
        const originalOverflow = pageEl.style.overflow;

        // Set exact dimensions for export - force letter size
        pageEl.style.transform = 'none';
        pageEl.style.width = `${PAGE_WIDTH}in`;
        pageEl.style.height = `${PAGE_HEIGHT}in`;
        pageEl.style.minHeight = `${PAGE_HEIGHT}in`;
        pageEl.style.overflow = 'hidden';

        const canvas = await html2canvas(pageEl, {
          scale: 2, // High quality (300 DPI equivalent)
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: pageEl.offsetWidth,
          height: pageEl.offsetHeight,
          windowWidth: pageEl.offsetWidth,
          windowHeight: pageEl.offsetHeight,
        });

        // Restore original styles
        pageEl.style.transform = originalTransform;
        pageEl.style.width = originalWidth;
        pageEl.style.minHeight = originalMinHeight;
        pageEl.style.height = originalHeight;
        pageEl.style.overflow = originalOverflow;

        // Convert to blob and download
        await new Promise<void>((resolve) => {
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              // Add page number suffix for multi-page exports
              const pageSuffix = pageElements.length > 1 ? `-page${i + 1}` : '';
              a.download = `flyer-${validDates.start}-${validDates.end}${pageSuffix}.png`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }
            resolve();
          }, 'image/png');
        });
      }

      toast({ title: `Image${pageElements.length > 1 ? 's' : ''} exported successfully (${pageElements.length} page${pageElements.length > 1 ? 's' : ''})` });
    } catch (error) {
      console.error('Export failed:', error);
      toast({ title: 'Export failed', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  // Format date range
  const formatDateRange = () => {
    const start = new Date(validDates.start);
    const end = new Date(validDates.end);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="min-h-screen bg-gray-100">
        <AdminNavigation />

        <div className="flex">
          {/* Left Panel - Product Picker */}
          <div className="w-80 bg-white border-r border-gray-200 h-[calc(100vh-64px)] overflow-hidden flex flex-col print:hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-lg mb-3">Add Products</h2>

            {/* Section Selector */}
            <div className="mb-3">
              <Label className="text-xs text-gray-500 mb-1 block">Add to Section</Label>
              <Select value={selectedSection} onValueChange={setSelectedSection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select section" />
                </SelectTrigger>
                <SelectContent>
                  {visibleSections.map((section) => (
                    <SelectItem key={section.id} value={section.id}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded ${section.headerColor}`} />
                        {section.title}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Product Thumbnails Grid - Virtualized 4 column layout */}
          <div className="flex-1 overflow-hidden p-2">
            {filteredProducts.length === 0 ? (
              <div className="text-center text-gray-400 py-8 text-sm">
                No products available
              </div>
            ) : (
              <Grid
                columnCount={PRODUCT_GRID_COLUMNS}
                columnWidth={56}
                height={500}
                rowCount={Math.ceil(filteredProducts.length / PRODUCT_GRID_COLUMNS)}
                rowHeight={72}
                width={240}
                cellComponent={SidebarProductCell}
                cellProps={{ products: filteredProducts }}
              />
            )}
            {filteredProducts.length > 100 && (
              <p className="text-xs text-center text-gray-400 mt-2">
                Showing {filteredProducts.length} products
              </p>
            )}
          </div>
        </div>

        {/* Right Panel - Flyer Preview */}
        <div className="flex-1 p-4 overflow-auto">
          {/* Toolbar */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4 print:hidden">
            {/* Top Row - Theme Selector */}
            <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Theme:</span>
              </div>
              <div className="flex gap-2">
                {THEMES.map((theme) => (
                  <ThemeThumbnail
                    key={theme.id}
                    theme={theme}
                    isSelected={selectedTheme.id === theme.id}
                    onClick={() => handleThemeChange(theme)}
                  />
                ))}
              </div>
              <div className="ml-auto flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowTemplateGallery(true)}
                  className="border-purple-300 text-purple-600 hover:bg-purple-50"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Templates
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSectionConfig(true)}
                >
                  <LayoutGrid className="w-4 h-4 mr-2" />
                  Sections
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowStoreConfig(true)}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Store Info
                </Button>

                {/* Template Save */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64">
                    <div className="space-y-2">
                      <Label>Template Name</Label>
                      <Input
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="My Weekly Flyer"
                      />
                      <Button onClick={handleSaveTemplate} className="w-full" size="sm">
                        Save Template
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Template Load */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Load
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      <Label>Saved Templates</Label>
                      {templates.length === 0 ? (
                        <p className="text-sm text-gray-500">No saved templates</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                          {templates.map((t) => (
                            <div
                              key={t.id}
                              className="group relative border rounded-lg overflow-hidden hover:border-blue-500 cursor-pointer transition-colors"
                              onClick={() => handleLoadTemplate(t)}
                            >
                              {/* Thumbnail */}
                              <div className="aspect-[8.5/11] bg-gray-100">
                                {t.thumbnail ? (
                                  <img
                                    src={t.thumbnail}
                                    alt={t.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <FileText className="w-8 h-8" />
                                  </div>
                                )}
                              </div>
                              {/* Info overlay */}
                              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-1.5">
                                <p className="text-xs font-medium truncate">{t.name}</p>
                                <p className="text-[10px] text-gray-300">
                                  {new Date(t.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                              {/* Delete button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t.id); }}
                                className="absolute top-1 right-1 h-5 w-5 p-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-red-500"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Separator */}
                <div className="h-6 w-px bg-gray-300" />

                {/* Undo/Redo */}
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={undo}
                    disabled={!canUndo}
                    title="Undo (Cmd+Z)"
                    className="px-2"
                  >
                    <Undo2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={redo}
                    disabled={!canRedo}
                    title="Redo (Cmd+Shift+Z)"
                    className="px-2"
                  >
                    <Redo2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Separator */}
                <div className="h-6 w-px bg-gray-300" />

                {/* Flyer Save (with products) */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="bg-green-50 border-green-200 hover:bg-green-100">
                      <FileArchive className="w-4 h-4 mr-2 text-green-600" />
                      Save Flyer
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64">
                    <div className="space-y-2">
                      <Label>Save Complete Flyer</Label>
                      <p className="text-xs text-gray-500">
                        Saves layout AND all products with prices/badges
                      </p>
                      <Input
                        value={flyerName}
                        onChange={(e) => {
                          setFlyerName(e.target.value);
                          if (flyerNameError && e.target.value.trim()) setFlyerNameError(false);
                        }}
                        placeholder="Week of Jan 6 Flyer"
                        className={flyerNameError ? 'border-red-500 focus:ring-red-500' : ''}
                      />
                      {flyerNameError && (
                        <p className="text-xs text-red-500">Flyer name is required</p>
                      )}
                      <Button onClick={handleSaveFlyer} className="w-full bg-green-600 hover:bg-green-700" size="sm">
                        Save Flyer
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Flyer Load */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="bg-green-50 border-green-200 hover:bg-green-100">
                      <FolderOpen className="w-4 h-4 mr-2 text-green-600" />
                      Load Flyer
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      <Label>Saved Flyers</Label>
                      <p className="text-xs text-gray-500">
                        Loads layout and all products
                      </p>
                      {savedFlyers.length === 0 ? (
                        <p className="text-sm text-gray-500 py-2">No saved flyers</p>
                      ) : (
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                          {savedFlyers.map((f) => (
                            <div key={f.id} className="flex items-center justify-between p-2 rounded hover:bg-gray-100 border">
                              <button
                                onClick={() => requestLoadFlyer(f)}
                                className="text-left flex-1"
                              >
                                <p className="text-sm font-medium">{f.name}</p>
                                <p className="text-xs text-gray-500">
                                  {new Date(f.createdAt).toLocaleDateString()} • {f.products.length} products
                                </p>
                              </button>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => exportFlyerAsJSON(f)}
                                  className="h-6 w-6 p-0 text-blue-500"
                                  title="Export as JSON"
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteFlyer(f.id)}
                                  className="h-6 w-6 p-0 text-red-500"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Separator */}
                <div className="h-6 w-px bg-gray-300" />

                {/* Data Management (Import/Export/Backup) */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      <HardDrive className="w-4 h-4 mr-2" />
                      Data
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56">
                    <div className="space-y-2">
                      <Label>Data Management</Label>
                      <div className="space-y-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleExportCurrentFlyer}
                          className="w-full justify-start"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Export Current Flyer
                        </Button>
                        <label className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border cursor-pointer hover:bg-gray-50">
                          <Upload className="w-4 h-4" />
                          Import Flyer JSON
                          <input
                            type="file"
                            accept=".json"
                            onChange={handleImportFlyer}
                            className="hidden"
                          />
                        </label>
                        <div className="border-t my-2" />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleBackup}
                          className="w-full justify-start"
                        >
                          <HardDrive className="w-4 h-4 mr-2" />
                          Backup All Data
                        </Button>
                        <label className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border cursor-pointer hover:bg-gray-50">
                          <Upload className="w-4 h-4" />
                          Restore from Backup
                          <input
                            type="file"
                            accept=".json"
                            onChange={handleRestore}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Auto-save indicator */}
                {lastAutoSave && (
                  <>
                    <div className="h-6 w-px bg-gray-300" />
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      <span>Auto-saved {lastAutoSave.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Bottom Row - Flyer Settings */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Title</Label>
                  <Button
                    variant="outline"
                    onClick={() => setShowTitleConfig(true)}
                    className="w-48 justify-start font-normal truncate"
                  >
                    <Type className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="truncate">{headerTitleConfig.title || 'Set Title...'}</span>
                  </Button>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Valid From</Label>
                  <Input
                    type="date"
                    value={validDates.start}
                    onChange={(e) => setValidDates(prev => ({ ...prev, start: e.target.value }))}
                    onBlur={() => setTimeout(recordSnapshot, 0)}
                    className="w-36"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Valid To</Label>
                  <Input
                    type="date"
                    value={validDates.end}
                    onChange={(e) => setValidDates(prev => ({ ...prev, end: e.target.value }))}
                    onBlur={() => setTimeout(recordSnapshot, 0)}
                    className="w-36"
                  />
                </div>
                {/* Date Presets */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Calendar className="w-4 h-4 mr-1" />
                      Quick
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-40" align="start">
                    <div className="space-y-1">
                      {DATE_PRESETS.map((preset) => (
                        <Button
                          key={preset.id}
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => handleDatePreset(preset.id)}
                        >
                          {preset.label}
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Card Design Selector */}
                <div>
                  <Label className="text-xs text-gray-500">Card Design</Label>
                  <div className="flex items-center gap-1">
                    <Select value={globalCardDesign} onValueChange={(v) => { setGlobalCardDesign(v as CardDesign); setTimeout(recordSnapshot, 0); }}>
                      <SelectTrigger className="w-36">
                        <Layers className="w-4 h-4 mr-1" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CARD_DESIGN_PRESETS.map((preset) => (
                          <SelectItem key={preset.id} value={preset.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{preset.label}</span>
                              <span className="text-xs text-gray-500">{preset.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        saveAppSettings({ defaultCardDesign: globalCardDesign });
                        toast({ title: 'Saved', description: `"${globalCardDesign}" is now your default card design` });
                      }}
                      title="Save as default"
                      className="h-9 w-9 p-0"
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Selection Mode Toggle */}
                <Button
                  variant={selectionMode ? "default" : "outline"}
                  onClick={() => {
                    if (selectionMode) {
                      clearSelection();
                    } else {
                      setSelectionMode(true);
                    }
                  }}
                  className={selectionMode ? "bg-blue-600 hover:bg-blue-700" : ""}
                >
                  <MousePointer2 className="w-4 h-4 mr-2" />
                  {selectionMode ? 'Exit Select' : 'Select'}
                </Button>

                {/* Bulk Action Bar - appears when products are selected */}
                {selectionMode && selectedProducts.size > 0 && (
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1">
                    <span className="text-sm font-medium text-blue-700">
                      {selectedProducts.size} selected
                    </span>

                    {/* Move */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7">
                          <Move className="w-3 h-3 mr-1" />
                          Move
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-48" align="start">
                        <div className="space-y-1">
                          <p className="text-xs text-gray-500 mb-2">Move to section:</p>
                          {visibleSections.map((section) => (
                            <Button
                              key={section.id}
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start"
                              onClick={() => bulkMoveSelected(section.id)}
                            >
                              <div className={`w-3 h-3 rounded ${section.headerColor} mr-2`} />
                              {section.title}
                            </Button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* Badge */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7">
                          <Tag className="w-3 h-3 mr-1" />
                          Badge
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-40" align="start">
                        <div className="space-y-1">
                          {PRODUCT_BADGES.map((badge) => (
                            <Button
                              key={badge.id}
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start"
                              onClick={() => bulkBadgeSelected(badge.id)}
                            >
                              <div className={`w-3 h-3 rounded ${badge.color} mr-2`} />
                              {badge.label}
                            </Button>
                          ))}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-gray-500"
                            onClick={() => bulkBadgeSelected(undefined)}
                          >
                            <X className="w-3 h-3 mr-2" />
                            Remove badge
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* Price */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7"
                      onClick={() => {
                        setBatchPriceScope('selected');
                        setShowBatchPriceDialog(true);
                      }}
                    >
                      <DollarSign className="w-3 h-3 mr-1" />
                      Price
                    </Button>

                    {/* Delete */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={bulkDeleteSelected}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>

                    {/* Clear Selection */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7"
                      onClick={clearSelection}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}

                <Button
                  variant="outline"
                  onClick={handleClearAllProducts}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" disabled={isExporting}>
                      {isExporting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Export
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-40" align="end">
                    <div className="space-y-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start"
                        onClick={handleExportPDF}
                        disabled={isExporting}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start"
                        onClick={handleExportPNG}
                        disabled={isExporting}
                      >
                        <FileImage className="w-4 h-4 mr-2" />
                        Download PNG
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button
                  variant="outline"
                  onClick={() => setPrintPreviewMode(true)}
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>
          </div>

          {/* Flyer Preview - 8.5x11" aspect ratio */}
          <div className="flex flex-col items-center gap-8">
            {/* Multi-page indicator */}
            {isMultiPage && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded-lg text-sm print:hidden">
                <span className="font-medium">{pages.length} pages</span> - Use page breaks in section settings to control pagination
              </div>
            )}

            {/* Scale indicator */}
            {flyerScale < 1 && (
              <div className="fixed top-20 right-4 bg-amber-100 border border-amber-300 text-amber-800 px-3 py-1.5 rounded-lg text-sm shadow-sm z-10 print:hidden">
                Auto-scaled to {Math.round(flyerScale * 100)}% to fit page
              </div>
            )}

            {/* Render each page */}
            <div ref={printRef} id="flyer-preview" className="flex flex-col gap-8 print:gap-0">
              {pages.map((pageSections, pageIndex) => (
                <div
                  key={pageIndex}
                  className="flyer-page relative bg-white shadow-xl origin-top print:shadow-none print:break-after-page"
                  style={{
                    width: '8.5in',
                    minHeight: '11in',
                    transform: `scale(${flyerScale})`,
                    transformOrigin: 'top center',
                  }}
                >
                  {/* Page number indicator (print hidden) */}
                  {isMultiPage && (
                    <div className="absolute top-2 right-2 bg-gray-800 text-white text-xs px-2 py-1 rounded print:hidden z-20">
                      Page {pageIndex + 1} of {pages.length}
                    </div>
                  )}

                  {/* Dynamic Header */}
                  {renderHeader(selectedTheme.headerStyle, {
                    theme: selectedTheme,
                    storeConfig,
                    titleConfig: headerTitleConfig,
                    dateRange: formatDateRange(),
                  })}

                  {/* Content - Dynamic Sections for this page */}
                  <div className="p-2 flex-1">
                    {pageSections.map((section) => (
                      <FlyerSection
                        key={section.id}
                        section={section}
                        products={flyerProducts[section.id] || []}
                        onRemoveProduct={(id) => removeProductFromSection(id, section.id)}
                        onUpdatePrice={(id, price) => updateProductPrice(id, section.id, price)}
                        onUpdateBadge={(id, badge) => updateProductBadge(id, section.id, badge)}
                        onEditSection={() => { setEditingSectionId(section.id); setShowSectionConfig(true); }}
                        onClearSection={() => clearSectionProducts(section.id)}
                        onAutoFill={() => autoFillSection(section.id)}
                        globalCardDesign={globalCardDesign}
                        selectionMode={selectionMode}
                        selectedProducts={selectedProducts}
                        onToggleSelect={(productId) => toggleProductSelection(section.id, productId)}
                      />
                    ))}
                  </div>

                  {/* Dynamic Footer */}
                  {renderFooter(selectedTheme.footerStyle, {
                    theme: selectedTheme,
                    storeConfig,
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section Configurator Dialog */}
      <SectionConfigurator
        sections={sections}
        onSectionsChange={(newSections) => { setSections(newSections); setTimeout(recordSnapshot, 0); }}
        isOpen={showSectionConfig}
        onClose={() => setShowSectionConfig(false)}
        availableCategories={categories}
      />

      {/* Store Config Dialog */}
      <StoreConfigDialog
        config={storeConfig}
        onConfigChange={(newConfig) => { setStoreConfig(newConfig); setTimeout(recordSnapshot, 0); }}
        isOpen={showStoreConfig}
        onClose={() => setShowStoreConfig(false)}
      />

      {/* Header Title Config Dialog */}
      <HeaderTitleConfigDialog
        config={headerTitleConfig}
        onConfigChange={(newConfig) => { setHeaderTitleConfig(newConfig); setTimeout(recordSnapshot, 0); }}
        isOpen={showTitleConfig}
        onClose={() => setShowTitleConfig(false)}
      />

      {/* Batch Price Dialog */}
      <BatchPriceDialog
        isOpen={showBatchPriceDialog}
        onClose={() => setShowBatchPriceDialog(false)}
        onApply={handleBatchPriceApply}
        productCount={getBatchPriceProductCount()}
        scope={batchPriceScope}
      />

      {/* Draft Recovery Dialog */}
      <AlertDialog open={showDraftRecoveryDialog} onOpenChange={setShowDraftRecoveryDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-blue-500" />
              Recover Unsaved Work?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>We found an auto-saved draft from your previous session.</p>
                {draftData?.savedAt && (
                  <p className="text-sm text-gray-500">
                    Last saved: {new Date(draftData.savedAt).toLocaleString()}
                  </p>
                )}
                <p className="text-sm">Would you like to restore it?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardDraft}>Discard</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestoreDraft} className="bg-blue-600 hover:bg-blue-700">
              Restore Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Load Flyer Confirmation Dialog */}
      <AlertDialog open={showLoadFlyerConfirm} onOpenChange={setShowLoadFlyerConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Replace Current Flyer?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>Loading this flyer will replace your current work.</p>
                <p className="text-sm text-gray-500">
                  You have products in your current flyer. This action cannot be undone.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowLoadFlyerConfirm(false); setPendingLoadFlyer(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmLoadFlyer} className="bg-amber-600 hover:bg-amber-700">
              Replace & Load
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Selected Products Confirmation Dialog */}
      <AlertDialog open={showDeleteSelectedConfirm} onOpenChange={setShowDeleteSelectedConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete Selected Products?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>This will remove {selectedProducts.size} products from the flyer.</p>
                <p className="text-sm text-gray-500">You can undo this action with Ctrl+Z.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                bulkDeleteSelected();
                setShowDeleteSelectedConfirm(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete {selectedProducts.size} Products
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template Gallery Dialog */}
      {showTemplateGallery && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Template Gallery</h2>
                  <p className="text-sm text-gray-500">Choose a starting point for your flyer</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTemplateGallery(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 p-4 border-b bg-gray-50">
              {(['all', 'weekly', 'holiday', 'clearance', 'special'] as const).map((cat) => (
                <Button
                  key={cat}
                  variant={selectedGalleryCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedGalleryCategory(cat)}
                  className={selectedGalleryCategory === cat ? 'bg-purple-600 hover:bg-purple-700' : ''}
                >
                  {cat === 'all' ? 'All Templates' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </Button>
              ))}
            </div>

            {/* Template Grid */}
            <div className="flex-1 overflow-auto p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {GALLERY_TEMPLATES
                  .filter(t => selectedGalleryCategory === 'all' || t.category === selectedGalleryCategory)
                  .map((template) => {
                    const theme = THEMES.find(t => t.id === template.themeId) || THEMES[0];
                    return (
                      <button
                        key={template.id}
                        onClick={() => handleApplyGalleryTemplate(template)}
                        className="group bg-white border-2 border-gray-200 rounded-lg p-3 text-left hover:border-purple-400 hover:shadow-lg transition-all"
                      >
                        {/* Thumbnail preview */}
                        <div
                          className={`h-24 rounded-lg mb-3 flex items-center justify-center text-3xl ${theme.colorScheme.headerBg}`}
                        >
                          {template.thumbnail}
                        </div>
                        {/* Info */}
                        <h3 className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                          {template.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {template.description}
                        </p>
                        {/* Tags */}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {template.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        {/* Sections preview */}
                        <div className="flex gap-1 mt-2">
                          {template.sections.slice(0, 4).map((section, i) => (
                            <div
                              key={i}
                              className={`w-3 h-3 rounded ${section.headerColor}`}
                              title={section.title}
                            />
                          ))}
                          {template.sections.length > 4 && (
                            <span className="text-[10px] text-gray-400">+{template.sections.length - 4}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t bg-gray-50">
              <p className="text-xs text-gray-500">
                Selecting a template will clear your current flyer and apply the new layout
              </p>
              <Button variant="outline" onClick={() => setShowTemplateGallery(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          #flyer-preview, #flyer-preview * {
            visibility: visible;
          }
          #flyer-preview {
            position: absolute;
            left: 0;
            top: 0;
            display: flex;
            flex-direction: column;
          }
          .flyer-page {
            width: 8.5in !important;
            height: 11in !important;
            min-height: 11in !important;
            max-height: 11in !important;
            transform: scale(1) !important;
            overflow: hidden;
            page-break-after: always;
            page-break-inside: avoid;
          }
          .flyer-page:last-child {
            page-break-after: auto;
          }
          @page {
            size: letter portrait;
            margin: 0;
          }
        }
      `}</style>
      </div>

      {/* Print Preview Mode */}
      {printPreviewMode && (
        <div className="print-preview-mode">
          {/* Preview toolbar */}
          <div className="print-preview-toolbar">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold">Print Preview</h2>
              <span className="text-gray-400 text-sm">
                {pages.length} page{pages.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Zoom controls */}
              <div className="flex items-center gap-2 bg-gray-700 rounded-lg px-2 py-1">
                <button
                  onClick={() => setPrintScale(s => Math.max(50, s - 10))}
                  className="p-1 hover:bg-gray-600 rounded"
                  title="Zoom out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-sm w-12 text-center">{printScale}%</span>
                <button
                  onClick={() => setPrintScale(s => Math.min(150, s + 10))}
                  className="p-1 hover:bg-gray-600 rounded"
                  title="Zoom in"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPrintScale(100)}
                  className="text-xs px-2 py-1 hover:bg-gray-600 rounded"
                  title="Reset zoom"
                >
                  Reset
                </button>
              </div>
              {/* Actions */}
              <Button
                onClick={() => {
                  setPrintPreviewMode(false);
                  setTimeout(handlePrint, 100);
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button
                variant="ghost"
                onClick={() => setPrintPreviewMode(false)}
                className="text-white hover:bg-gray-700"
              >
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>

          {/* Preview content */}
          <div className="print-preview-content">
            {pages.map((pageSections, pageIndex) => (
              <div
                key={pageIndex}
                className="flyer-page relative bg-white"
                style={{
                  width: '8.5in',
                  height: '11in',
                  transform: `scale(${printScale / 100})`,
                  transformOrigin: 'top center',
                  marginBottom: printScale < 100 ? `-${11 * (1 - printScale / 100)}in` : '0',
                }}
              >
                {/* Page number */}
                {pages.length > 1 && (
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-white text-sm">
                    Page {pageIndex + 1} of {pages.length}
                  </div>
                )}

                {/* Header */}
                {renderHeader(selectedTheme.headerStyle, {
                  theme: selectedTheme,
                  storeConfig,
                  titleConfig: headerTitleConfig,
                  dateRange: formatDateRange(),
                })}

                {/* Content */}
                <div className="p-2 flex-1">
                  {pageSections.map((section) => (
                    <FlyerSection
                      key={section.id}
                      section={section}
                      products={flyerProducts[section.id] || []}
                      globalCardDesign={globalCardDesign}
                      selectionMode={false}
                      selectedProducts={new Set()}
                    />
                  ))}
                </div>

                {/* Footer */}
                {renderFooter(selectedTheme.footerStyle, {
                  theme: selectedTheme,
                  storeConfig,
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drag Overlay */}
      <DragOverlay>
        {activeProduct && <DragOverlayContent product={activeProduct} />}
      </DragOverlay>
    </DndContext>
  );
}
